import type {
    RangeSelectorProps,
    Range,
    MultiSliderProps,
} from '@archipro-design/aria';
import {
    RangeSelector,
    pxToRem,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import type { FilterStatesKey } from '../../type/filter-state';
import { useEffect, useRef, useState } from 'react';

export interface RangeSelectorFilter {
    kind: 'RangeSelector';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
    props: RangeSelectorProps;
    displayTitle?: string;
    /** Decimal places typed values accept, in display units. Whole numbers only when unset. */
    decimalPlaces?: number;
}

export type RangeSelectorFilterState = Pick<
    RangeSelectorProps,
    'range' | 'value'
> &
    Pick<RangeSelectorFilter, 'kind'>;

interface FilterRangeSelectorProps {
    filter: RangeSelectorFilter;
    state: RangeSelectorFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<RangeSelectorFilterState>,
    ) => void;
    disabled?: boolean;
}

/** The aria RangeSelector's own default input format, kept when no precision is set. */
const DEFAULT_RANGE_INPUT_FORMAT = { thousandSeparator: ',' };

const FilterRangeSelector = ({
    filter,
    state,
    updateFilter,
    disabled,
}: FilterRangeSelectorProps) => {
    const { props, filterKey, decimalPlaces } = filter;
    // Typed values keep up to this many decimals (e.g. 35.5 in); without it
    // the selector keeps its default format.
    const rangeInputFormat =
        decimalPlaces === undefined
            ? props.rangeInputFormat
            : {
                  ...DEFAULT_RANGE_INPUT_FORMAT,
                  ...props.rangeInputFormat,
                  maxDecimalPlaces: decimalPlaces,
              };

    const { desktop } = useAppDisplayModeContext();

    const valueChanged = useRef(false);

    const [localState, setLocalState] =
        useState<RangeSelectorFilterState>(state);

    useEffect(() => {
        setLocalState(state);
    }, [state]);

    const updateValue = (value?: Range) => {
        if (!valueChanged.current) return;
        updateFilter(filterKey, { kind: 'RangeSelector', value });
        valueChanged.current = false;
    };

    return (
        <RangeSelector
            {...props}
            rangeInputFormat={rangeInputFormat}
            disabled={disabled}
            range={localState?.range ?? props.range}
            value={localState.value ?? null}
            onChange={(e, data) => {
                valueChanged.current = true;
                setLocalState({ ...localState, value: data?.value });
                // Update URL if numerical input changes
                if (e.type === 'blur') {
                    updateValue(data?.value);
                }
            }}
            // Update URL on user interaction end events (prevent overfetching)
            multiSlider={{
                onMouseUp: (
                    _: React.SyntheticEvent,
                    data?: Omit<MultiSliderProps, 'value'> & { value: Range },
                ) => {
                    updateValue(data?.value);
                },
                onTouchEnd: (
                    _: React.SyntheticEvent,
                    data?: Omit<MultiSliderProps, 'value'> & { value: Range },
                ) => {
                    updateValue(data?.value);
                },
                variables: {
                    thumbSize: desktop ? pxToRem(20) : pxToRem(28),
                },
            }}
        />
    );
};

export default FilterRangeSelector;
